import { getDateString, getDiffDays } from './date'

// stayRules comes from /api/availability (StayRuleCalendar's StayRulesStruct):
// stays { 'Y-m-d': [nights, …] }, blockedArrivals { 'Y-m-d': reason }, checkInDays { 'Y-m-d': [weekday, …] },
// minimumStays { 'Y-m-d': nights }, nightMinimums { 'Y-m-d': nights }, closedDepartures ['Y-m-d', …]

// StayRuleCalendar::MAX_NIGHTS, the longest stay the site sells
export const MAX_NIGHTS = 28
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const joinOr = words => words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`
// weekdays read Monday to Sunday, so a weekend is "Saturday or Sunday"
const byWeekOrder = (a, b) => (a + 6) % 7 - (b + 6) % 7
const nameDays = days => joinOr([...days].sort(byWeekOrder).map(day => WEEKDAYS[day]))
const weekdayOf = dateString => new Date(dateString).getUTCDay()
const addDays = (dateString, days) => {
    const date = new Date(dateString)
    date.setUTCDate(date.getUTCDate() + days)
    return date
}
const closedDeparturesOf = (() => {
    const sets = new WeakMap()
    return stayRules => {
        if (!sets.has(stayRules)) sets.set(stayRules, new Set(stayRules.closedDepartures ?? []))
        return sets.get(stayRules)
    }
})()

// the ticket's wording: a week reads "7-Day", anything else "N-Night"
const minimumMessage = nights => nights === 7 ? '7-Day Minimum Required' : `${nights}-Night Minimum Required`

// the longest minimum a stay of these nights must meet: its arrival's, and any night's that binds on every stay through it
function requiredNights (stayRules, arrival, nights) {
    let required = stayRules.minimumStays?.[arrival] ?? 1
    for (let offset = 0; offset < nights; offset++) {
        required = Math.max(required, stayRules.nightMinimums?.[getDateString(addDays(arrival, offset))] ?? 1)
    }
    return required
}

// whether any check-out within reach of this arrival is closed, i.e. the resort fixes its check-out days
function hasClosedCheckOut (stayRules, arrival) {
    const closed = closedDeparturesOf(stayRules)
    for (let nights = 1; nights <= MAX_NIGHTS; nights++) {
        if (closed.has(getDateString(addDays(arrival, nights)))) return true
    }
    return false
}

// the weekdays named in "Saturday Check-in Required", or null when they cannot be named
function allowedWeekdays (dateStrings) {
    const days = new Set(dateStrings.map(weekdayOf))
    return days.size && days.size < 7 ? nameDays(days) : null
}

function arrivalMessage (stayRules, reason, dateString) {
    if (reason === 'check_in_day') {
        // the night's own weekdays, so a seasonal rule is named as it applies on that date
        const days = stayRules.checkInDays?.[dateString] ?? []
        return days.length ? `${nameDays(days)} Check-in Required` : 'Check-in Not Available'
    }
    return reason === 'minimum_stay' ? 'Too Few Nights Available' : 'Check-in Not Available'
}

/**
 * The tooltip saying why a date can't be chosen under the resort's stay rules, or null
 * when no rule speaks for it and the night-by-night availability decides, as before.
 */
export function getRuleBlock ({ stayRules, arrival, selecting, date, isAvailable }) {
    if (!stayRules) return null
    const arrivalDate = arrival && new Date(arrival)

    // a check-out picked before any arrival has no stay to judge yet
    if (selecting === 'departure' && !arrivalDate) return null

    // a date before the arrival starts a new selection, so it is judged as an arrival
    if (selecting === 'departure' && arrivalDate && date > arrivalDate) {
        const lengths = stayRules.stays[arrival]
        if (!lengths?.length) return null
        const nights = getDiffDays([ date, arrivalDate ])
        if (lengths.includes(nights)) return null
        // the search button explains stays past the 28-night limit
        if (nights > MAX_NIGHTS) return null
        for (let night = new Date(arrivalDate); night < date; night.setUTCDate(night.getUTCDate() + 1)) {
            if (!isAvailable(night)) return null
        }
        const required = requiredNights(stayRules, arrival, nights)
        if (nights < required) return minimumMessage(required)
        if (closedDeparturesOf(stayRules).has(getDateString(date))) {
            const days = allowedWeekdays(lengths.map(length => getDateString(addDays(arrival, length))))
            return days ? `${days} Check-Out Required` : 'Check-Out Not Available'
        }
        // nights the merged calendar shows open can still belong to different residences, none selling them all
        return 'Stay Not Available'
    }

    if (!isAvailable(date)) return null
    const dateString = getDateString(date)
    const reason = stayRules.blockedArrivals[dateString]
    return reason ? arrivalMessage(stayRules, reason, dateString) : null
}

/**
 * The days a resort lets a stay start or end where it restricts them (Saturday-to-Saturday), as
 * 'Y-m-d' → 'arrival' | 'departure' for the calendar's Arrive Only / Depart Only marks.
 */
export function getRuleCues ({ stayRules, arrival, selecting }) {
    const cues = new Map()
    if (!stayRules) return cues
    const choosingDeparture = selecting === 'departure' && !!arrival

    for (const dateString of Object.keys(stayRules.checkInDays ?? {})) {
        // past the arrival a click picks the check-out, so only the dates before it can start a stay
        if (choosingDeparture && dateString > arrival) continue
        if (stayRules.stays[dateString]) {
            cues.set(dateString, 'arrival')
        }
    }

    if (choosingDeparture && hasClosedCheckOut(stayRules, arrival)) {
        (stayRules.stays[arrival] ?? []).forEach(length => cues.set(getDateString(addDays(arrival, length)), 'departure'))
    }
    return cues
}

/**
 * Whether the stay from arrival to departure is one the resort sells; true without stay rules,
 * so a form keeps its dates as before.
 */
export function isRuleStay (stayRules, arrival, departure) {
    if (!stayRules || !arrival || !departure) return true
    const nights = getDiffDays([ new Date(departure), new Date(arrival) ])
    return nights > MAX_NIGHTS || !!stayRules.stays[arrival]?.includes(nights)
}

/**
 * The check-out a weekly resort fills in with its arrival: the minimum stay, when that is a week or more.
 */
export function getDefaultDeparture (stayRules, arrival) {
    const minimum = stayRules?.minimumStays?.[arrival]
    return minimum >= 7 && stayRules.stays[arrival]?.includes(minimum) ? getDateString(addDays(arrival, minimum)) : null
}

// "2-night minimum when choosing Friday or Saturday": one-night stays except on some weekdays
function weekdayMinimumNote (stayRules, arrivals) {
    const minimumsByDay = new Map()
    for (const dateString of arrivals) {
        const day = weekdayOf(dateString)
        minimumsByDay.set(day, new Set([...(minimumsByDay.get(day) ?? []), stayRules.minimumStays?.[dateString] ?? 1]))
    }
    if ([...minimumsByDay.values()].some(minimums => minimums.size > 1)) return null

    const minimumOf = day => [...minimumsByDay.get(day)][0]
    const longerDays = [...minimumsByDay.keys()].filter(day => minimumOf(day) > 1)
    const longer = new Set(longerDays.map(minimumOf))
    if (longer.size !== 1 || longerDays.length === minimumsByDay.size) return null

    return `${[...longer][0]}-night minimum when choosing ${nameDays(longerDays)}`
}

/**
 * The page banner for the one rule every arrival shares ("Saturday check-in, 7-night minimum stay"),
 * or null when the arrivals follow different rules.
 *
 * @param {string[]|null} months 'Y-m' to limit the arrivals to, or null for the whole calendar
 */
export function getStayRuleNote (stayRules, months = null) {
    if (!stayRules) return null
    const inMonths = dateString => !months || months.includes(dateString.slice(0, 7))
    const arrivals = Object.keys(stayRules.stays).filter(inMonths)
    if (!arrivals.length) return null

    const parts = []
    // one rule only if every night with inventory names the same check-in days, sold-out weekdays included
    const nights = [...arrivals, ...Object.keys(stayRules.blockedArrivals).filter(inMonths)]
    const checkInDays = new Set(nights.map(dateString => JSON.stringify(stayRules.checkInDays?.[dateString] ?? null)))
    const [onlyDays] = checkInDays
    const isOneCheckInDayRule = checkInDays.size === 1 && onlyDays !== 'null'
    if (isOneCheckInDayRule) {
        parts.push(`${nameDays(JSON.parse(onlyDays))} check-in`)
    }

    const minimums = new Set(arrivals.map(dateString => stayRules.minimumStays?.[dateString] ?? 1))
    const [minimum] = minimums
    if (minimums.size === 1 && minimum > 1) {
        parts.push(`${minimum}-night minimum stay`)
    } else if (!isOneCheckInDayRule) {
        const note = weekdayMinimumNote(stayRules, arrivals)
        if (note) parts.push(note)
    }

    return parts.length ? parts.join(', ') : null
}
